import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/slices/authSlice';
import { 
  LayoutDashboard, 
  FileText, 
  CreditCard, 
  User, 
  LogOut,
  Settings,
  Menu,
  X,
  BarChart3,
  Cog,
  Package,
  Users
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const hasClientManagement = user?.subscription?.plan_id?.features?.clientManagement;

  const userNav = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Invoices', path: '/invoices', icon: FileText },
    ...(hasClientManagement ? [{ name: 'Clients', path: '/clients', icon: Users }] : []),
    { name: 'Subscriptions', path: '/subscriptions', icon: CreditCard },
    { name: 'Profile', path: '/profile', icon: User },
  ];

  const navItems = user?.role === 'admin'
    ? [
        { name: 'Admin Panel', path: '/admin', icon: BarChart3 },
        { name: 'Traffic', path: '/admin/traffic', icon: BarChart3 },
        { name: 'Plans', path: '/admin/plans', icon: Package },
        { name: 'Settings', path: '/admin/settings', icon: Cog },
        { name: 'My Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Invoices', path: '/invoices', icon: FileText },
        ...(hasClientManagement ? [{ name: 'Clients', path: '/clients', icon: Users }] : []),
        { name: 'Subscriptions', path: '/subscriptions', icon: CreditCard },
        { name: 'Profile', path: '/profile', icon: User },
      ]
    : userNav;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        <div className="flex items-center justify-between h-16 px-6 border-b">
          <h1 className="text-xl font-bold text-primary-600">Ainix Invoice</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <nav className="p-4 space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full p-4 border-t">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 w-full text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                {user?.subscription?.status === 'active' && user?.subscription?.plan_id && (
                  <p className="text-xs text-gray-500">
                    {user.subscription.plan_id.name} Plan
                  </p>
                )}
              </div>
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-primary-600" />
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;

